import { ACTIVITY_ENDPOINT } from "./session-activity.js";
import { getActiveLocale, translate } from "./locale.js";
import { isConfirmedCommandResponse, responseErrorCode } from "./system-session.js";

// Story 1.7 / FR-4: безопасные описания auth-ошибок локализуются по семантическому
// ключу каталога. `error.localeKey` — ключ, который UI рендерит через `t()` в
// текущем locale; `message` — безопасный текст текущего locale (fallback для
// не-UI потребителей). Семантика (коды, статусы, отсутствие эха тела) не меняется.
function authError(localeKey) {
  const error = new Error(translate(getActiveLocale(), localeKey));
  error.localeKey = localeKey;
  return error;
}

// TC-1: cookie-auth mutations требуют exact Origin (браузер сам) и CSRF-заголовок.
const CSRF_HEADER = "X-FreeHub-CSRF";
const CSRF_VALUE = "1";
const UNSAFE_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

// Единый epoch сессии. Ответ 401 прежней сессии (late response) не должен
// завершать новую session/UI; epoch увеличивается при login/logout/expiry.
let sessionEpoch = 0;
// Operation ownership: принадлежность security-команды конкретной сессии.
// Растёт вместе с появлением/завершением сессии (вход, подтверждённый выход,
// восстановленная session), но НЕ при истечении времени жизни ТОЙ ЖЕ сессии.
// Разделение нужно потому, что подтверждённый сервером результат команды не
// должен теряться из-за фонового 401 её же сессии, и при этом никогда не должен
// публиковаться в новую сессию.
let sessionIdentity = 0;
let unauthorizedHandler = null;

export function currentSessionEpoch() {
  return sessionEpoch;
}

export function currentSessionIdentity() {
  return sessionIdentity;
}

// Смена сессии: и lifetime-fence (epoch), и владелец операций (identity).
export function advanceSessionEpoch() {
  sessionEpoch += 1;
  sessionIdentity += 1;
  return sessionEpoch;
}

// Lifetime-fence ТОЙ ЖЕ сессии (истечение/ревок): late-ответы прежнего lifetime
// не публикуются, но уже стартовавшая security-команда этой же сессии остаётся
// её собственной — identity не меняется.
export function fenceSessionLifetime() {
  sessionEpoch += 1;
  return sessionEpoch;
}

// Consumer-фенс: session-scoped потребитель запоминает epoch до запроса и после
// await проверяет, что session не сменилась (logout/expiry/новый вход). Иначе
// поздний ответ прежней сессии не должен менять состояние новой session/UI.
export function captureSessionEpoch() {
  return sessionEpoch;
}

export function isSessionEpochCurrent(epoch) {
  return epoch === sessionEpoch;
}

export function captureSessionIdentity() {
  return sessionIdentity;
}

export function isSessionIdentityCurrent(identity) {
  return identity === sessionIdentity;
}

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = typeof handler === "function" ? handler : null;
}

function normalizedMethod(init) {
  return String((init && init.method) || "GET").toUpperCase();
}

function requestHeaders(init, method) {
  // Принимаем любую валидную HeadersInit-форму (Headers, массив пар, object):
  // object spread над Headers/массивом терял заголовки. normalize через Headers
  // сохраняет Content-Type/Accept и не подменяет multipart boundary, а CSRF
  // выставляется case-insensitively и не дублируется.
  const headers = new Headers((init && init.headers) || undefined);
  if (UNSAFE_METHODS.includes(method)) {
    headers.set(CSRF_HEADER, CSRF_VALUE);
  }
  return headers;
}

async function readJson(response, fallbackKey) {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.toLowerCase().includes("application/json")) {
    if (response.status === 413) {
      throw authError("error.auth_request_oversized");
    }
    if (response.status >= 500) {
      throw authError("error.auth_unavailable");
    }
    throw authError(fallbackKey);
  }

  try {
    return await response.json();
  } catch (_error) {
    throw authError(fallbackKey);
  }
}

/**
 * Единая точка cookie-fetch. Добавляет CSRF-заголовок всем unsafe-методам
 * (login/logout/activity, бизнес-записи и upload), сохраняет
 * `credentials: "same-origin"` и при 401 передаёт управление
 * централизованному обработчику сессии — только если ответ принадлежит
 * текущему epoch. Отказ не превращается в автоматический повтор mutation.
 *
 * `localCredentialRejection: true` (Story 1.6, S16-S2) — per-call флаг для
 * `password/change`: неверный **текущий пароль** приходит тем же 401, что и
 * истёкшая сессия, но сессия при этом остаётся действительной. С флагом тело
 * читается из копии и централизованный путь вызывается только для
 * `authentication_required`; `invalid_credentials` возвращается вызывающему как
 * локальный безопасный отказ. Неразобранное тело/отсутствующий code — fail
 * closed к централизованному пути. Для остальных вызовов поведение прежнее:
 * тело не читается, второй state-owner не вводится.
 */
export async function apiFetch(input, init = {}) {
  const { localCredentialRejection = false, ...rest } = init;
  const method = normalizedMethod(init);
  const requestEpoch = sessionEpoch;
  const response = await fetch(input, {
    ...rest,
    headers: requestHeaders(init, method),
    credentials: "same-origin",
  });

  if (
    response.status === 401 &&
    requestEpoch === sessionEpoch &&
    typeof unauthorizedHandler === "function"
  ) {
    const localRejection =
      localCredentialRejection === true &&
      (await responseErrorCode(response)) === "invalid_credentials";
    // Чтение тела асинхронное: за это время epoch мог смениться
    // (logout/expiry/новый вход). Любой auth-side-effect выполняется только
    // если ответ всё ещё принадлежит ТЕКУЩЕМУ epoch; иначе поздний 401 прежней
    // сессии завершил бы уже новую.
    if (!localRejection && requestEpoch === sessionEpoch) {
      unauthorizedHandler({ url: String(input), method });
    }
  }

  return response;
}

export async function currentSession(timeoutMs = 4_000) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch("/api/auth/current", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    if (!response.ok) {
      throw authError("error.auth_session_unavailable");
    }
    return await readJson(response, "error.auth_invalid_response");
  } catch (error) {
    if (error?.name === "AbortError") {
      throw authError("error.auth_session_timeout");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function ownerBootstrapStatus() {
  const response = await fetch("/api/auth/bootstrap", {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  const payload = await readJson(response, "error.auth_bootstrap_check_failed");
  if (!response.ok) {
    throw authError(response.status >= 500
      ? "error.auth_bootstrap_unavailable"
      : "error.auth_bootstrap_check_failed");
  }
  if (payload.status !== "required" && payload.status !== "complete") {
    throw authError("error.auth_bootstrap_status_invalid");
  }
  return payload.status;
}

export async function createOwner(username, password) {
  const response = await fetch("/api/auth/bootstrap", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      [CSRF_HEADER]: CSRF_VALUE,
    },
    // Fetch supplies the exact current Origin for this same-origin POST.
    body: JSON.stringify({ username, password }),
  });
  const payload = await readJson(response, "error.auth_bootstrap_response_invalid");
  if (response.status === 409) return { conflict: true };
  if (response.status === 503) {
    throw authError("error.auth_owner_unavailable");
  }
  if (response.status === 422) {
    throw authError("error.auth_bootstrap_request_invalid");
  }
  if (!response.ok || response.status !== 201 || payload.status !== "complete") {
    throw authError("error.auth_owner_create_failed");
  }
  return { conflict: false };
}

export async function login(username, password) {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      [CSRF_HEADER]: CSRF_VALUE,
    },
    body: JSON.stringify({ username, password }),
  });
  const session = await readJson(response, "error.auth_bootstrap_response_invalid");
  if (response.status === 401) {
    throw authError("error.auth_invalid_credentials");
  }
  if (response.status === 429) {
    throw authError("error.auth_rate_limited");
  }
  if (response.status === 503) {
    throw authError("error.auth_unavailable");
  }
  if (!response.ok) {
    throw authError("error.auth_unavailable");
  }
  if (!session.authenticated) {
    throw authError("error.auth_session_create_failed");
  }
  return session;
}

// Возвращает { confirmed }, где confirmed=true означает серверное подтверждение
// отзыва сессии. 503/сетевой сбой не выдаётся за подтверждённый выход.
export async function logout() {
  try {
    const response = await apiFetch("/api/auth/logout", {
      method: "POST",
      headers: { Accept: "application/json" },
    });
    return { confirmed: response.ok };
  } catch (_error) {
    return { confirmed: false };
  }
}

// Story 1.6: завершение ВСЕХ административных сессий владельца (включая текущую).
// `confirmed === true` только при точном контракте: HTTP 200 + parseable
// anonymous-тело. 201/204/иной 2xx, неразбираемое тело и тело с
// `authenticated: true` не считаются успехом. Автоматического повтора нет.
//
// `code` берётся из тела только у отказного (не-2xx) ответа: у неожиданного
// success-ответа произвольный `code` из тела — не зарегистрированный отказ, а
// неизвестный исход (`unconfirmed`).
function refusalCode(response, code) {
  return response.status >= 200 && response.status < 300 ? "unconfirmed" : code || "unconfirmed";
}

export async function logoutAll() {
  try {
    const response = await apiFetch("/api/auth/logout-all", {
      method: "POST",
      headers: { Accept: "application/json" },
    });
    if (!(await isConfirmedCommandResponse(response))) {
      return { confirmed: false, code: refusalCode(response, await responseErrorCode(response)) };
    }
    return { confirmed: true, code: "" };
  } catch (error) {
    return { confirmed: false, code: error?.name === "AbortError" ? "aborted" : "network" };
  }
}

// Story 1.6: смена пароля владельца с завершением всех сессий.
// `changed === true` только при том же точном контракте 200 + anonymous-тело.
// Неверный `current_password` приходит локальным 401 `invalid_credentials`
// (`localCredentialRejection`) и **не** завершает сессию; истёкшая сессия
// (`authentication_required`) по-прежнему идёт в централизованный путь.
export async function changePassword(currentPassword, newPassword) {
  try {
    const response = await apiFetch("/api/auth/password/change", {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
      localCredentialRejection: true,
    });
    if (!(await isConfirmedCommandResponse(response))) {
      return { changed: false, code: refusalCode(response, await responseErrorCode(response)) };
    }
    return { changed: true, code: "" };
  } catch (error) {
    return { changed: false, code: error?.name === "AbortError" ? "aborted" : "network" };
  }
}

// Обновляет idle TTL текущей session. Client timestamp не отправляется. Ответ
// 401 обрабатывается централизованно в apiFetch (cleanup), не глотается здесь и
// не порождает автоматический повтор mutation.
export async function sendSessionActivity() {
  return apiFetch(ACTIVITY_ENDPOINT, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
}
